import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';

import { Button } from '@/ui';
import Spinner from '@/ui/components/Spinner';
import { cn } from '@/ui/lib/utils';

import { DEFAULT_QUERY_BUILDER_LABELS } from './defaultLabels';
import LogicGroupEditor from './LogicGroupEditor';
import { QueryBuilderConfigProvider } from './QueryBuilderContext';
import type { QueryBuilderConfig, QueryBuilderProps as Props } from './types';

/**
 * Generic, fully-controlled criteria-editing form for building an AND/OR tree
 * of conditions (LogicGroups/LogicElements) against a set of `fieldOptions`.
 * This component owns no data-fetching or API calls -- pass in the current
 * `fieldOptions`/`rootGroups` plus CRUD callbacks (typically backed by a hook
 * like `useLogicQueryBuilder`) and it renders and drives the whole tree.
 *
 * Every piece of built-in text, whether nested groups/multiple root groups
 * are allowed, where each group's action buttons sit, and how a condition's
 * inputs lay out can all be customized via props -- see `QueryBuilderProps`.
 */
function QueryBuilder({
  fieldOptions,
  rootGroups,
  isLoading = false,
  onAddRootGroup,
  onAddChildGroup,
  onUpdateGroup,
  onDeleteGroup,
  onAddElement,
  onChangeElement,
  onDeleteElement,
  className,
  labels,
  allowNestedGroups = true,
  maxNestingDepth,
  allowMultipleRootGroups = true,
  groupActionsPosition = 'bottom',
  elementLayout = 'wrap',
  hideFieldSelect = false,
  hideOperatorSelect = false,
  hideNegate = false,
  allowMultipleValues = true,
  classNames = {}
}: Props) {
  const config: QueryBuilderConfig = useMemo(
    () => ({
      labels: { ...DEFAULT_QUERY_BUILDER_LABELS, ...labels },
      allowNestedGroups,
      maxNestingDepth,
      groupActionsPosition,
      elementLayout,
      hideFieldSelect,
      hideOperatorSelect,
      hideNegate,
      allowMultipleValues,
      classNames
    }),
    [
      labels,
      allowNestedGroups,
      maxNestingDepth,
      groupActionsPosition,
      elementLayout,
      hideFieldSelect,
      hideOperatorSelect,
      hideNegate,
      allowMultipleValues,
      classNames
    ]
  );

  if (isLoading) {
    return <Spinner label={config.labels.loading} />;
  }

  if (fieldOptions.length === 0) {
    return <p className="text-sm text-uniform-gray-600">{config.labels.noFields}</p>;
  }

  return (
    <QueryBuilderConfigProvider value={config}>
      <div className={cn(classNames.root, className)}>
        <div className="flex flex-col gap-3">
          {rootGroups.map((group) => (
            <LogicGroupEditor
              key={group.id}
              group={group}
              fieldOptions={fieldOptions}
              depth={0}
              canRemove={rootGroups.length > 1}
              onUpdateGroup={onUpdateGroup}
              onDeleteGroup={onDeleteGroup}
              onAddChildGroup={onAddChildGroup}
              onAddElement={onAddElement}
              onChangeElement={onChangeElement}
              onDeleteElement={onDeleteElement}
            />
          ))}
        </div>

        {(allowMultipleRootGroups || rootGroups.length === 0) && (
          <Button
            aria-label={config.labels.addRootGroup}
            variant="outline"
            color="secondary"
            size="sm"
            className="mt-3"
            onClick={onAddRootGroup}>
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            {config.labels.addRootGroup}
          </Button>
        )}
      </div>
    </QueryBuilderConfigProvider>
  );
}

const MemoizedQueryBuilder = React.memo(QueryBuilder);

export { MemoizedQueryBuilder as QueryBuilder };
